import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import {
  compose,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
  returnAddress,
} from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Modal from '../../../commonComponents/modal/Modal';
import SubscriptionProp from '../propTypes/subscriptionProp';
import Form from '../../../commonComponents/forms/Form';
import FormAddress from '../../../commonComponents/forms/Address/FormAddress';
import Anchor from '../../../commonComponents/Anchor';
import Notifications from '../../../services/notifications';
import { addressReducer } from '../../../commonComponents/modal/AcademyPaymentModal';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import UserProp from '../../../commonComponents/propTypes/userProp';
import Loader from '../../../services/loader';

function UpdateAddressModal({ t, onClose, subscription, _csrf, countries, user, isShipping }) {
  const browserHistory = useHistory();
  const loaderModal = useRef(new Loader('#address-update'));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (loaderModal.current) {
      if (isLoading.modal) {
        loaderModal.current.show();
      } else {
        loaderModal.current.hide();
      }
    }
  }, [isLoading]);

  const validationRules = {
    firstName: { required: true },
    lastName: { required: true },
    address_1: { required: true },
    city: { required: true },
    zipCode: { required: true },
    countryCode: { required: true },
    phone: { maxlength: 20 },
  };

  // INIT PROPS
  const { loggedUser } = useContext(LoggedUserContext);
  const initialAddress = isShipping ? handleShippingAddressInOrder(
    loggedUser,
    subscription.shippingAddress,
  ) : handleBillingAddressInOrder(
    loggedUser,
    subscription.billingAddress,
  );

  const initialStateAddress = {
    billingAddress: {},
    showAddressForm: false,
  };

  const [address, dispatchAddressSection] = useReducer(
    addressReducer,
    initialStateAddress,
    () => ({
      ...initialStateAddress,
      billingAddress: {
        ...initialAddress,
      },
      countryCode: initialAddress.countryCode,
    }),
  );
  const { billingAddress } = address;
  const handleAddressForm = show =>
    dispatchAddressSection({ type: 'showAddressForm', payload: show });

  const handleAddressChange = fieldParam => value => {
    dispatchAddressSection({
      type: 'updateAddress',
      payload: {
        [fieldParam]: value,
      },
    });
  };
  const saveAddress = () => {
    setIsLoading(true);
    let params;
    if (isShipping) {
      params = {
        shippingAddress: { ...billingAddress, address: returnAddress(billingAddress) },
      };
    } else {
      params = {
        billingAddress: { ...billingAddress, address: returnAddress(billingAddress) },
      };
    }
    axios({
      url: `/api/market/order/subscription/${subscription._id}/update`,
      method: 'PUT',
      data: { ...params },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(({ data: response }) => {
        if (response) {
          handleAddressForm(false);
          setIsLoading(false);
          Notifications.showNotificationSuccess(
            t('success'),
            t('order.subscription.shippingAddress.success'),
          );
          browserHistory.replace(`${window.location.pathname}?open=true`);
          browserHistory.go(0);
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'order.subscription.shippingAddress.error');
        console.log(err);
        setIsLoading(false);
      });
  };

  return (
    <Modal onClose={onClose} id="address-update">
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isShipping ? t('order.subscription.editShippingAddress') : t('order.subscription.editBillingAddress')}
            </h5>
          </div>
          <div className="modal-body">
            <div className="add-new-address">
              <Form
                id="yourBillingAddress"
                submitMethod={saveAddress}
                validationRules={validationRules}
              >
                {submitHandler => (
                  <>
                    <FormAddress
                      countries={countries}
                      address={billingAddress}
                      onFieldChange={handleAddressChange}
                      isShippingAddress={isShipping}
                      countryToFilter={(billingAddress && billingAddress.countryCode) || null}
                      user={user}
                      googleSearchLabelClass="col-form-label "
                    />
                    <br />
                    <div className="handle-form-address">
                      <Anchor
                        className="secondary"
                        onClick={onClose}
                      >
                        {t('back')}
                      </Anchor>
                      <Anchor
                        className={`btn btn-dynamic primary ${
                          isLoading && 'm-loader m-loader--right m-loader--light'
                        }`}
                        onClick={submitHandler}
                      >
                        {t('save')}
                      </Anchor>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

UpdateAddressModal.propTypes = {
  t: PropTypes.func.isRequired,
  subscription: PropTypes.shape(SubscriptionProp.propType).isRequired,
  onClose: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  isShipping: PropTypes.bool,
};

UpdateAddressModal.defaultProps = {
  isShipping: false,
};

export default compose(withCSRF, translate)(UpdateAddressModal);
